<template>
    <div v-show="isAddNotice == false" class="main_div" :key="componentKey">
        <div style="height: auto">
            <n-space>
                <NCard style="margin-top: 20px; margin-left: 80px; border: 0px; min-width: 800px">
                    <n-space item-style="display: flex;" align="center">
                        <div style="font-size: 20px; width: 150px; font-weight: bold">공지 관리</div>
                    </n-space>
                    <div class="content_delimiter_line"></div>
                    <Loading v-show="loading" />
                    <div>
                        <button @click="showAddNotice" class="action-button">공지 등록</button>
                        <div v-if="listLoading" class="loading-indicator">공지 목록을 불러오는 중...</div>
                        <div v-if="error" class="error-message">{{ error }}</div>
                        <div v-if="!listLoading && !error">
                            <div class="table-container">
                                <n-data-table
                                    v-if="noticeList.length > 0"
                                    :columns="columns"
                                    :data="noticeList"
                                    :pagination="false"
                                    :bordered="true"
                                />
                                <p v-else>등록된 공지가 없습니다.</p>
                            </div>
                            <div
                                v-if="noticeList.length > 0"
                                class="pagination-controls"
                                style="margin-top: 15px; text-align: center"
                            >
                                <n-button
                                    @click="wrappedGoToPreviousPage"
                                    :disabled="currentPage === 1"
                                    style="margin-right: 8px"
                                >
                                    이전 페이지
                                </n-button>
                                <span style="margin: 0 10px">{{ currentPage }} 페이지</span>
                                <n-button @click="wrappedGoToNextPage" :disabled="!hasNextPage"> 다음 페이지 </n-button>
                            </div>
                        </div>
                    </div>
                </NCard>
                <NModal
                    v-model:show="isAlert"
                    :mask-closable="false"
                    :content="alertMessage"
                    :type="alertType"
                    preset="dialog"
                    title="Dialog"
                    positive-text="확인"
                    @positive-click="onPositiveClick"
                >
                </NModal>
                <NModal v-model:show="isShowContent" @after-leave="onContentModalClose">
                    <n-card
                        style="width: 800px; max-height: 80vh; overflow-y: auto"
                        title="공지 내용"
                        :bordered="false"
                        size="huge"
                        role="dialog"
                        aria-modal="true"
                    >
                        <div
                            v-if="isContentLoading"
                            class="loading-indicator"
                            style="text-align: center; padding: 20px"
                        >
                            내용을 불러오는 중...
                        </div>
                        <div v-else-if="contentError" class="error-message" style="color: red; padding: 20px">
                            {{ contentError }}
                        </div>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div v-else v-html="contentMessage"></div>
                        <template #footer>
                            <n-button @click="isShowContent = false">닫기</n-button>
                        </template>
                    </n-card>
                </NModal>
            </n-space>
        </div>
    </div>
    <AddNotice
        v-show="isAddNotice == true"
        @hide="hideAddNotice"
        :isAddNotice="isAddNotice"
        :selectedItem="selectedItem"
    >
    </AddNotice>
</template>

<script setup lang="ts">
import { ref, onMounted, h } from "vue";
import { getCallable, makeCallApiParam } from "@/config/env";
import {
    NDataTable,
    NSpace,
    NCheckbox,
    NCard,
    NSelect,
    NButton,
    NIcon,
    NModal,
    NRadio,
    NRadioGroup,
    NInput,
    FormInst,
    FormRules,
    useMessage,
    useNotification,
    useDialog,
} from "naive-ui";
import Loading from "@/components/Loading.vue";
import * as common from "@/assets/common/common";
import * as constant from "@/constants/constant";
import AddNotice from "@/views/notice/AddNotice.vue";
import { Board } from "@/model/board";
import { QueryInfo } from "@/model/common";
import { usePagination } from "@/composables/usePagination";
import { useAuth } from "@/composables/useAuth";
import { useActionDialog } from "@/composables/useDialog";
const { show: showActionDialog } = useActionDialog();

const { loading, isLoggedIn } = useAuth();

const message = useMessage();
const notification = useNotification();
const dialog = useDialog();

const getCallApiCallable = getCallable("callApi");
const getBoards = "getBoards";
const getBoardContent = "getBoardContent";
const deleteNoticeCallable = getCallable("deleteBoard");

const componentKey = ref(0);
const deleteConfirmMessage = ref<string | null>(null);
const contentMessage = ref<string | null>(null);
const alertMessage = ref<string | null>(null);
const alertType = ref<string | null>(null);
const isDeleteConfirm = ref<boolean>(false);
const deleteConfirmType = ref<string | null>(null);
const isAddNotice = ref<boolean>(false);
const isShowContent = ref<boolean>(false);
const isAlert = ref<boolean>(false);
const isDeleting = ref<boolean>(false);
const isContentLoading = ref<boolean>(false);
const contentError = ref<string | null>(null);
const selectedItem = ref<Board | null>(null);

const {
    paginatedItems: noticeList,
    listLoading,
    error,
    currentPage,
    hasNextPage,
    goToNextPage: composableGoToNextPage,
    goToPreviousPage: composableGoToPreviousPage,
    refreshData: composableRefreshNotices,
} = usePagination<Board>(
    async (params) =>
        getCallApiCallable(
            makeCallApiParam(getBoards, {
                ...params,
                type: constant.boardType.NOTICE,
            })
        ),
    25 // 페이지 크기
);

const registrationRules: FormRules = {
    title: [
        {
            required: true,
            message: "제목을 입력해주세요.",
            trigger: ["input", "blur"],
        },
    ],
    content: [
        {
            required: true,
            message: "내용을 입력해주세요.",
            trigger: ["input", "blur"],
        },
    ],
};

async function wrappedRefreshNotices() {
    loading.value = true; // 전역 로딩 시작
    await composableRefreshNotices();
    loading.value = false; // 전역 로딩 종료
}

const columns = [
    {
        title: "index",
        key: "index",
        resizable: true,
        render(_row: Board, rowIndex: number) {
            return rowIndex + 1;
        },
    },
    {
        title: "제목",
        key: "title",
        resizable: true,
    },
    {
        title: "내용",
        sorter: "content_drive_id",
        render(row: Board) {
            return h(
                NButton,
                {
                    size: "small",
                    onClick: () => {
                        onShowContent(row);
                    },
                    bordered: true,
                },
                { default: () => "내용 보기" }
            );
        },
    },
    {
        title: "작성자",
        key: "author",
        resizable: true,
    },
    {
        title: "작성일",
        key: "createdDate",
        render(row: Board) {
            return common.getDate(row.createdDate);
        },
    },
    {
        title: "삭제",
        sorter: "default",
        render(row: Board) {
            return h(
                NButton,
                {
                    size: "small",
                    onClick: () => {
                        deleteNoticeItem(row);
                    },
                    bordered: true,
                },
                { default: () => "삭제" }
            );
        },
    },
];

function showAddNotice() {
    isAddNotice.value = true;
}

async function hideAddNotice() {
    selectedItem.value = null;
    isAddNotice.value = false;
    await wrappedRefreshNotices();
}
async function onShowContent(row: Board) {
    isShowContent.value = true;
    contentMessage.value = ""; // 이전 내용 초기화
    contentError.value = null; // 이전 오류 초기화

    if (row.content_drive_id) {
        isContentLoading.value = true;
        try {
            const result = await getCallApiCallable(makeCallApiParam(getBoardContent, { id: row.content_drive_id }));
            if (common.evalFunctionResult(result)) {
                const content = common.getItemFromFunctionResult(result);
                contentMessage.value = content;
            } else {
                throw new Error(common.getResultError(result) || "공지 내용 로드 실패");
            }
        } catch (err: any) {
            console.error("공지 내용 로드 실패 (content_drive_id):", err);
            contentError.value = `외부 URL에서 내용을 불러오는데 실패했습니다. (오류: ${err.message}). HTML 원본 내용을 표시합니다.`;
            contentMessage.value = row.content || "표시할 내용이 없습니다."; // HTML 원본 내용으로 대체
        } finally {
            isContentLoading.value = false;
        }
    } else {
        contentMessage.value = row.content || "표시할 내용이 없습니다.";
        if (!row.content) {
            contentError.value = "표시할 내용 URL이 없으며, 저장된 HTML 원본 내용도 없습니다.";
        }
    }
}

function onPositiveClick() {
    isAlert.value = false;
}

async function wrappedGoToNextPage() {
    if (hasNextPage.value) {
        await composableGoToNextPage();
    }
}

async function wrappedGoToPreviousPage() {
    if (currentPage.value > 1) {
        await composableGoToPreviousPage();
    }
}
function deleteNoticeItem(notice: Board) {
    showActionDialog({
        title: "확인",
        content: "공지사항을 하시겠습니까?",
        positiveText: "삭제",
        negativeText: "아니요",
        positiveButtonProps: { type: "error", loading: isDeleting.value },
        confirmButtonLoadingText: "삭제 중...",
        onConfirm: async () => {
            try {
                const result = await deleteNoticeCallable({
                    boardType: constant.boardType.NOTICE,
                    SK: notice.SK,
                });
                if (common.evalFunctionResult(result)) {
                    message.success("공지사항 삭제가 완료되었습니다.");
                    await wrappedRefreshNotices();
                } else {
                    throw new Error(common.getResultError(result) || "공지사항 삭제 중 오류가 발생했습니다.");
                }
            } catch (error: any) {
                console.error("공지사항 삭제 API 호출 실패:", error);
                message.error(common.getResultError(error) || "공지사항 삭제 중 오류가 발생했습니다.");
            }
        },
    });
}

function onContentModalClose() {
    contentMessage.value = "";
    contentError.value = null;
    isContentLoading.value = false;
}

onMounted(() => {
    wrappedRefreshNotices();
});
</script>

<style scoped></style>
